import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { Subscription } from 'rxjs';
import { OrgQuarterSelectorComponent } from './core/org-quarter-selector/org-quarter-selector.component';
import { DialogMessageService } from './services/dialog-message.service';
import { LoadingSpinnerService } from './services/loading-spinner.service';
import { MsalHelperService } from './services/msal-helper.service';
import { OrgManagementService } from './services/org-management.service';
import { QuarterSelectorService } from './services/quarter-selector.service';
import { SysUtilService } from './services/sys-util.service';
import { UserService } from './services/user.service';
import { Organization } from './types/organization';
import { Quarter } from './types/quarter';
import { User } from './types/user';
import { Version } from './types/version';


@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
    template: `Number of ticks: {{ numberOfTicks }}`,
    changeDetection: ChangeDetectionStrategy.Default,
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('oqcontext') oqSelector: OrgQuarterSelectorComponent;
  user: User;
  title = 'FASB_138542';
  userName = 'Not Logged In';
  loginDisplay = false;
  loginValidated: boolean = null;
  selectedOrg: Organization;
  selectedQtr: Quarter;
  isLoading = false;
  versionData: Version = { version: '0.0.0.0-dev' };
  env = 'env';
  loadingSpinnerUpdator: Subscription;
  userUpdateSubscription: Subscription;
  numberOfTicks = 0;

  public constructor(
    public readonly titleService: Title,
    public readonly msalHelper: MsalHelperService,
    private readonly authService: MsalService,
    public readonly selectorService: QuarterSelectorService,
    private readonly router: Router,
    private readonly messageService: DialogMessageService,
    private readonly userService: UserService,
    private readonly utilService: SysUtilService,
    private readonly loadingSpinnerService: LoadingSpinnerService,
    private readonly orgManagementService: OrgManagementService,
    private readonly changeDetectionRef: ChangeDetectorRef
  ) {
    setInterval(() => {
      this.numberOfTicks++;
      // require view to be updated
      this.changeDetectionRef.markForCheck();
    }, 200);
  }

  ngOnInit() {
    this.titleService.setTitle('ASC 450');
    this.msalHelper.runAfterAuthenticated(() => {
      this.userService.currentUser.subscribe({
        next: user => {
          if (user != null) {
            this.user = user;
            this.loginValidated = user.active;
          }
        },
        error: err => this.loginValidated = false,
        complete: () => {
          // Anytime someone changes the selected org, render the users login name
          this.selectorService.selectedContextChange.subscribe({
            next: (value) => {
              // display a snackbar if the quarter or the organization is changed
              // quarter changes if organization changes due to their type relationship
              // display quarter snackbar before organization snackbar to prevent override
              if (this.selectedQtr !== value.qtr) {
                this.selectorService.displayOrgOrQtrChanged('Quarter was changed!', 1.5);
              }
              if (this.selectedOrg !== value.org) {
                this.selectorService.displayOrgOrQtrChanged('Organization was changed!', 1.5);
              }
              this.selectedQtr = value.qtr;
              this.selectedOrg = value.org;
              this.setLoginDisplay();
            },
            error: (err) => {
              this.messageService.openErrorMessage(err);
              this.isLoading = false;
            }
          });
          this.msalHelper.updateLastLogin().subscribe({
            next: () => console.log("success")
          });
          this.userService.getVersion().subscribe({
            next: (value) => this.versionData = value
          });
          this.utilService.getEnv().subscribe({
            next: (value) => this.env = value.value
          });
          this.loadingSpinnerUpdator = this.loadingSpinnerService.getLoadingSpinnerUpdates().subscribe({
            next: (value) => {
              this.isLoading = value;
              this.changeDetectionRef.detectChanges();
            }
          });
        }
      });

      //// Anytime someone changes the route, render the users login name
      this.router.events.subscribe({
        next: (val) => this.setLoginDisplay(),
        error: (err) => {
          this.messageService.openErrorMessage(err);
          this.isLoading = false;
        }
      });
    });
    this.userUpdateSubscription = this.orgManagementService.getNewOrgSubscription().subscribe({
      next: () => {
        this.userService.getCurrentUser().subscribe({
          next: (result) => {
            this.user = result;
            this.oqSelector.organizations = result.organizations.sort(this.oqSelector.sortOrg);
            this.oqSelector.chooseSelectedOrg();
          },
          error: (err) => this.user = new User()
        }).add(() => this.loadingSpinnerService.disable());
      }
    });
  }

  ngOnDestroy() {
    this.loadingSpinnerUpdator.unsubscribe();
    this.userUpdateSubscription.unsubscribe();
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
    if (this.loginDisplay) {
      this.userName = this.authService.instance.getActiveAccount().name;
      // Only add Role detail if it's main app, and there is a selected org.  
      if (this.selectedOrg && !this.isAdmin() && !this.isTodo()) {
        this.userName += ` (${this.selectorService.getCurrentRoleAsTitle()})`;
      }
    }
  }

  logout() {
    this.authService.logout({
      postLogoutRedirectUri: null
    });
  }

  isAdmin(): boolean {
    return this.router.url.startsWith('/admin');
  }

  isTodo(): boolean {
    return this.router.url.startsWith('/todo');
  }
}
